import { useNavigate } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Modal } from "../../components/ui/modal";
import { CheckCircle, PlusCircle, MinusCircle } from "lucide-react";
import ListboxComponent from "../../components/common/ListboxComponent";
import { styles } from "../../styles/pages/Product/ProductAddStyle";

// FileUpload component
interface FileUploadProps {
    onFileSelect: (file: File | null) => void;
    onRemove?: () => void;
    index: number;
}

const FileUpload: React.FC<FileUploadProps> = ({ onFileSelect, onRemove, index }) => {
    const [fileName, setFileName] = useState("No file chosen");

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] || null;
        setFileName(file ? file.name : "No file chosen");
        onFileSelect(file);
    };

    return (
        <div className={styles.fileUploadContainer}>
            <label
                htmlFor={`profileFile-${index}`}
                className={styles.fileUploadButton}
            >
                Choose Image
            </label>
            <input
                id={`profileFile-${index}`}
                type="file"
                className="hidden"
                onChange={handleChange}
            />
            <span className={styles.fileUploadText}>
                {fileName}
            </span>
            {onRemove && (
                <button
                    type="button"
                    onClick={onRemove}
                    className={styles.fileUploadRemoveButton}
                >
                    <MinusCircle className="w-5 h-5" />
                </button>
            )}
        </div>
    );
};

const ProductAdd = () => {
    const navigate = useNavigate();
    const [prodName, setProdName] = useState("");
    const [prodPrice, setProdPrice] = useState("");
    const [prodDesc, setProdDesc] = useState("");
    const [prodCost, setProdCost] = useState("");
    const [prodType, setProdType] = useState("");
    const [prodUrl, setProdUrl] = useState("");
    const [prodPartName, setProdPartName] = useState("");
    const [prodPartNumber, setProdPartNumber] = useState("");
    const [prodCatId, setProdCatId] = useState("");
    const [prodSubCatId, setProdSubCatId] = useState("");
    const [prodImages, setProdImages] = useState<{ id: string; file: File | null }[]>([
        { id: crypto.randomUUID(), file: null },
    ]);
    const [prodIgst, setProdIgst] = useState("");
    const [prodSgst, setProdSgst] = useState("");
    const [prodCgst, setProdCgst] = useState("");
    const [gstType, setGstType] = useState<"IGST" | "CGST+SGST" | null>(null);
    const [saving, setSaving] = useState(false);
    const [categoryList, setCategoryList] = useState<any[]>([]);
    const [subCategoryList, setSubCategoryList] = useState<any[]>([]);
    const [errors, setErrors] = useState<{
        prod_name?: string;
        prod_price?: string;
        prod_desc?: string;
        prod_cost?: string;
        prod_type?: string;
        prod_url?: string;
        prod_part_name?: string;
        prod_part_number?: string;
        prod_cat_id?: string;
        prod_sub_cat_id?: string;
        prod_img1?: string;
        prod_igst?: string;
        prod_sgst?: string;
        prod_cgst?: string;
    }>({});
    const [isSuccessModalOpen, setSuccessModalOpen] = useState(false);

    useEffect(() => {
        const token = sessionStorage.getItem("access_token");

        if (!token) {
            setErrors({ prod_name: "Authorization required. Please log in." });
            navigate("/signin");
            return;
        }

        // Fetch categories
        axios
            .get(`${process.env.REACT_APP_API_URL}/api/category/name/list`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            })
            .then((response) => {
                if (response.data && Array.isArray(response.data.data)) {
                    const formattedCategories = response.data.data.map((cat: any) => ({
                        id: cat.id,
                        name: cat.category_name,
                    }));
                    setCategoryList(formattedCategories);
                }
            })
            .catch((error) => {
                console.error("Failed to fetch categories", error);
                if (error.response?.status === 401) {
                    setErrors({ prod_name: "Session expired. Please log in again." });
                    navigate("/signin");
                }
            });

        // Fetch GST type
        axios
            .get(`${process.env.REACT_APP_API_URL}/api/companywisegst`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            })
            .then((response) => {
                if (response.data.status === 200 && response.data.data.length > 0) {
                    const gstType = response.data.data[0].gst_type;
                    setGstType(gstType);
                } else {
                    console.error("Invalid GST response");
                    setErrors({ prod_name: "Failed to fetch GST configuration." });
                }
            })
            .catch((error) => {
                console.error("Failed to fetch GST type", error);
                setErrors({ prod_name: "Failed to fetch GST configuration." });
                if (error.response?.status === 401) {
                    navigate("/signin");
                }
            });
    }, [navigate]);

    const fetchSubCategories = useCallback(async (categoryId: string) => {
        const token = sessionStorage.getItem("access_token");
        if (!token) {
            setErrors({ prod_name: "Authorization required. Please log in." });
            navigate("/signin");
            return;
        }

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/api/subcategory/name/list`,
                { category_id: categoryId },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (response.data.status === 200) {
                const formattedSubCategories = Array.isArray(response.data.data)
                    ? response.data.data.map((sub: any) => ({
                        id: sub.id,
                        subcategory_name: sub.subcategory_name,
                    }))
                    : [];
                setSubCategoryList(formattedSubCategories);
            } else {
                setSubCategoryList([]);
            }
        } catch (error) {
            console.error("Error fetching subcategories", error);
            setSubCategoryList([]);
        }
    }, [navigate]);

    useEffect(() => {
        if (prodCatId) {
            fetchSubCategories(prodCatId);
        } else {
            setSubCategoryList([]);
            setProdSubCatId("");
        }
    }, [prodCatId, fetchSubCategories]);

    const handleAddFileUpload = () => {
        if (prodImages.length < 3) {
            setProdImages([...prodImages, { id: crypto.randomUUID(), file: null }]);
        }
    };

    const handleRemoveFileUpload = (idToRemove: string) => {
        const newImages = prodImages.filter((img) => img.id !== idToRemove);
        setProdImages(newImages);
    };

    const handleFileSelect = (id: string) => (file: File | null) => {
        const newImages = prodImages.map((img) =>
            img.id === id ? { ...img, file } : img
        );
        setProdImages(newImages);

        if (prodImages[0]?.id === id && file) {
            setErrors({ ...errors, prod_img1: undefined });
        }
    };

    const handleNumericInput = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        setter: React.Dispatch<React.SetStateAction<string>>,
        field: string
    ) => {
        const value = e.target.value.replace(/[^0-9.]/g, "");
        setter(value);

        const numericValue = Number(value);

        const isGSTField = ["prod_igst", "prod_cgst", "prod_sgst"].includes(field);
        const isOutOfRange = isGSTField && (numericValue < 1 || numericValue > 100);

        setErrors((prev) => ({
            ...prev,
            [field]: !value
                ? `The ${field.replace("prod_", "").replace(/_/g, " ")} field is required`
                : isNaN(numericValue) || numericValue < 0
                    ? `The ${field.replace("prod_", "").replace(/_/g, " ")} must be a valid non-negative number`
                    : isOutOfRange
                        ? `The ${field.replace("prod_", "").replace(/_/g, " ")} must be between 1 and 100`
                        : undefined,
        }));
    };


    const handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const { name, value } = e.target;
        setErrors((prev) => ({
            ...prev,
            [name]: value.trim() ? undefined : `The ${name.replace("prod_", "").replace(/_/g, " ")} field is required`,
        }));
        switch (name) {
            case "prod_name":
                setProdName(value);
                break;
            case "prod_desc":
                setProdDesc(value);
                break;
            case "prod_type":
                setProdType(value);
                break;
            case "prod_url":
                setProdUrl(value);
                break;
            case "prod_part_name":
                setProdPartName(value);
                break;
            case "prod_part_number":
                setProdPartNumber(value);
                break;
            default:
                break;
        }
    };

    const handleDropdownChange = (field: string) => (selected: any) => {
        setErrors((prev) => ({ ...prev, [field]: undefined }));
        if (field === "prod_cat_id") {
            setProdCatId(selected?.id || "");
            setProdSubCatId("");
            if (!selected?.id) {
                setErrors((prev) => ({ ...prev, prod_cat_id: "The Category field is required" }));
            }
        } else if (field === "prod_sub_cat_id") {
            setProdSubCatId(selected?.id || "");
        }
    };

    const validateForm = () => {
        const newErrors: typeof errors = {};

        if (!prodName.trim()) {
            newErrors.prod_name = "The Product name field is required";
        }

        if (!prodPrice.trim()) {
            newErrors.prod_price = "The Product price field is required";
        } else if (isNaN(Number(prodPrice)) || Number(prodPrice) <= 0) {
            newErrors.prod_price = "The Product price must be a valid positive number";
        }

        if (!prodCatId) {
            newErrors.prod_cat_id = "The Category field is required";
        }

        if (!prodImages[0]) {
            newErrors.prod_img1 = "At least one product image is required";
        }

        if (gstType === "IGST") {
            if (!prodIgst.trim()) {
                newErrors.prod_igst = "The IGST field is required";
            } else if (
                isNaN(Number(prodIgst)) ||
                Number(prodIgst) < 1 ||
                Number(prodIgst) > 100
            ) {
                newErrors.prod_igst = "IGST must be between 1 and 100";
            }
        } else if (gstType === "CGST+SGST") {
            if (!prodSgst.trim()) {
                newErrors.prod_sgst = "The SGST field is required";
            } else if (
                isNaN(Number(prodSgst)) ||
                Number(prodSgst) < 1 ||
                Number(prodSgst) > 100
            ) {
                newErrors.prod_sgst = "SGST must be between 1 and 100";
            }

            if (!prodCgst.trim()) {
                newErrors.prod_cgst = "The CGST field is required";
            } else if (
                isNaN(Number(prodCgst)) ||
                Number(prodCgst) < 1 ||
                Number(prodCgst) > 100
            ) {
                newErrors.prod_cgst = "CGST must be between 1 and 100";
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setSaving(true);
        setErrors({});

        if (!validateForm()) {
            setSaving(false);
            return;
        }

        const formData = new FormData();
        formData.append("prod_name", prodName);
        formData.append("prod_price", prodPrice);
        formData.append("prod_desc", prodDesc);
        formData.append("prod_cost", prodCost);
        formData.append("status", "1");
        formData.append("prod_type", prodType);
        formData.append("prod_url", prodUrl);
        formData.append("prod_part_name", prodPartName);
        formData.append("prod_part_number", prodPartNumber);
        formData.append("prod_cat_id", prodCatId);
        formData.append("prod_sub_cat_id", prodSubCatId || "");

        if (gstType === "IGST") {
            formData.append("IGST", prodIgst);
            formData.append("SGST", "0");
            formData.append("CGST", "0");
        } else if (gstType === "CGST+SGST") {
            formData.append("IGST", "0");
            formData.append("SGST", prodSgst);
            formData.append("CGST", prodCgst);
        }

        prodImages.forEach((img, index) => {
            if (img.file) {
                formData.append(`prod_img${index + 1}`, img.file);
            }
        });


        const token = sessionStorage.getItem("access_token");
        if (!token) {
            setErrors({ prod_name: "Authorization required. Please log in." });
            setSaving(false);
            navigate("/signin");
            return;
        }

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/api/product/create`,
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            if (response.data.status === 200) {
                setSuccessModalOpen(true);
                setTimeout(() => {
                    setSuccessModalOpen(false);
                    navigate("/product-list");
                }, 2000);
                setProdName("");
                setProdPrice("");
                setProdDesc("");
                setProdCost("");
                setProdType("");
                setProdUrl("");
                setProdPartName("");
                setProdPartNumber("");
                setProdCatId("");
                setProdSubCatId("");
                setProdImages([{ id: crypto.randomUUID(), file: null }]);
                setProdIgst("");
                setProdSgst("");
                setProdCgst("");
            } else if (response.data.status === 422 && response.data.message) {
                setErrors(
                    Object.fromEntries(
                        Object.entries(response.data.message).map(([key, value]) => [
                            key,
                            Array.isArray(value) ? value[0] : value,
                        ])
                    )
                );
            } else {
                setErrors({
                    prod_name: response.data.message || "Something went wrong.",
                });
            }
        } catch (error: any) {
            const errorMessage =
                error.response?.data?.message || "Failed to add product. Please try again.";
            setErrors({ prod_name: errorMessage });
            if (error.response?.status === 422 && error.response.data.errors) {
                setErrors(
                    Object.fromEntries(
                        Object.entries(error.response.data.errors).map(([key, value]) => [
                            key,
                            Array.isArray(value) ? value[0] : value,
                        ])
                    )
                );
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h2 className={styles.title}>Add Product</h2>
            </div>

            <div className={styles.formContainer}>
                <form className={styles.form} onSubmit={handleSubmit}>
                    <div className={styles.inputContainer}>
                        {/* Product Name */}
                        <div>
                            <label className={styles.label}>
                                Name <span className={styles.required}>*</span>
                            </label>
                            <input
                                type="text"
                                name="prod_name"
                                value={prodName}
                                onChange={handleInputChange}
                                className={`${styles.input} ${errors.prod_name ? styles.inputError : styles.inputDefault}`}
                            />
                            {errors.prod_name && (
                                <span className={styles.errorText}>{errors.prod_name}</span>
                            )}
                        </div>

                        {/* Part Name */}
                        <div>
                            <label className={styles.label}>Part Name</label>
                            <input
                                type="text"
                                name="prod_part_name"
                                value={prodPartName}
                                onChange={handleInputChange}
                                className={`${styles.input} ${styles.inputDefault}`}
                            />
                        </div>

                        {/* Part Number */}
                        <div>
                            <label className={styles.label}>Part Number</label>
                            <input
                                type="text"
                                name="prod_part_number"
                                value={prodPartNumber}
                                onChange={handleInputChange}
                                className={`${styles.input} ${styles.inputDefault}`}
                            />
                        </div>

                        {/* Product Type */}
                        <div>
                            <label className={styles.label}>Type</label>
                            <input
                                type="text"
                                name="prod_type"
                                value={prodType}
                                onChange={handleInputChange}
                                className={`${styles.input} ${styles.inputDefault}`}
                            />
                        </div>

                        {/* Product Price */}
                        <div>
                            <label className={styles.label}>
                                Price <span className={styles.required}>*</span>
                            </label>
                            <input
                                type="text"
                                name="prod_price"
                                value={prodPrice}
                                onChange={(e) => handleNumericInput(e, setProdPrice, "prod_price")}
                                className={`${styles.input} ${errors.prod_price ? styles.inputError : styles.inputDefault}`}
                            />
                            {errors.prod_price && (
                                <span className={styles.errorText}>{errors.prod_price}</span>
                            )}
                        </div>

                        {/* Product Cost */}
                        <div>
                            <label className={styles.label}>Cost</label>
                            <input
                                type="text"
                                name="prod_cost"
                                value={prodCost}
                                onChange={(e) => handleNumericInput(e, setProdCost, "prod_cost")}
                                className={`${styles.input} ${styles.inputDefault}`}
                            />
                            {errors.prod_cost && (
                                <span className={styles.errorText}>{errors.prod_cost}</span>
                            )}
                        </div>

                        {/* Category */}
                        <div>
                            <ListboxComponent
                                value={categoryList.find((cat) => cat.id === Number(prodCatId)) ?? null}
                                onChange={handleDropdownChange("prod_cat_id")}
                                items={categoryList}
                                label="Category"
                                error={errors.prod_cat_id}
                                displayKey="name"
                                required
                            />
                        </div>

                        {/* Sub Category */}
                        <div>
                            <ListboxComponent
                                value={subCategoryList.find((sub) => sub.id === Number(prodSubCatId)) ?? null}
                                onChange={handleDropdownChange("prod_sub_cat_id")}
                                items={subCategoryList}
                                label="Sub Category"
                                error={errors.prod_sub_cat_id}
                                displayKey="subcategory_name"
                                required={false}
                            />
                        </div>

                        {/* URL */}
                        <div>
                            <label className={styles.label}>URL</label>
                            <input
                                type="text"
                                name="prod_url"
                                value={prodUrl}
                                onChange={handleInputChange}
                                className={`${styles.input} ${styles.inputDefault}`}
                            />
                        </div>

                        {/* IGST */}
                        {gstType === "IGST" && (
                            <div>
                                <label className={styles.label}>
                                    IGST <span className={styles.required}>*</span>
                                </label>
                                <input
                                    type="text"
                                    name="prod_igst"
                                    value={prodIgst}
                                    inputMode="decimal"
                                    maxLength={3}
                                    onChange={(e) => handleNumericInput(e, setProdIgst, "prod_igst")}
                                    className={`${styles.input} ${errors.prod_igst ? styles.inputError : styles.inputDefault}`}
                                />
                                {errors.prod_igst && (
                                    <span className={styles.errorText}>{errors.prod_igst}</span>
                                )}
                            </div>
                        )}

                        {/* CGST + SGST */}
                        {gstType === "CGST+SGST" && (
                            <div className="col-span-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className={styles.label}>
                                        CGST <span className={styles.required}>*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="prod_cgst"
                                        value={prodCgst}
                                        inputMode="decimal"
                                        maxLength={3}
                                        onChange={(e) => handleNumericInput(e, setProdCgst, "prod_cgst")}
                                        className={`${styles.input} ${errors.prod_cgst ? styles.inputError : styles.inputDefault}`}
                                    />
                                    {errors.prod_cgst && (
                                        <span className={styles.errorText}>{errors.prod_cgst}</span>
                                    )}
                                </div>
                                <div>
                                    <label className={styles.label}>
                                        SGST <span className={styles.required}>*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="prod_sgst"
                                        value={prodSgst}
                                        inputMode="decimal"
                                        maxLength={3}
                                        onChange={(e) => handleNumericInput(e, setProdSgst, "prod_sgst")}
                                        className={`${styles.input} ${errors.prod_sgst ? styles.inputError : styles.inputDefault}`}
                                    />
                                    {errors.prod_sgst && (
                                        <span className={styles.errorText}>{errors.prod_sgst}</span>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Description */}
                        <div className="col-span-1 md:col-span-2">
                            <label className={styles.label}>Description</label>
                            <textarea
                                name="prod_desc"
                                value={prodDesc}
                                onChange={handleInputChange}
                                className={`${styles.input} min-h-[100px]`}
                            />
                        </div>

                        {/* File Upload Section */}
                        <div className="col-span-1 md:col-span-3 space-y-3">
                            {prodImages.map((img, index) => (
                                <div key={img.id}>
                                    <FileUpload
                                        index={index}
                                        onFileSelect={handleFileSelect(img.id)}
                                        onRemove={index > 0 ? () => handleRemoveFileUpload(img.id) : undefined}
                                    />

                                    {index === 0 && errors.prod_img1 && (
                                        <span className={styles.errorText}>{errors.prod_img1}</span>
                                    )}
                                </div>
                            ))}
                            {prodImages.length < 3 && (
                                <button
                                    type="button"
                                    onClick={handleAddFileUpload}
                                    className={styles.addFileButton}
                                >
                                    <PlusCircle className="w-5 h-5" />
                                    <span>Add Another Image</span>
                                </button>
                            )}
                        </div>
                    </div>

                    <div className={styles.buttonsContainer}>
                        <button className={styles.submitButton} type="submit" disabled={saving}>
                            {saving ? "Saving..." : "Submit"}
                        </button>
                        <button
                            className={styles.cancelButton}
                            type="button"
                            onClick={() => navigate("/product-list")}
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </div>

            <Modal
                isOpen={isSuccessModalOpen}
                onClose={() => setSuccessModalOpen(false)}
                className={styles.modal}
            >
                <button
                    onClick={() => setSuccessModalOpen(false)}
                    className={styles.modalCloseButton}
                ></button>
                <div className={styles.modalIconContainer}>
                    <CheckCircle className="w-6 h-6" />
                </div>
                <h4 className={styles.modalTitle}>Product Added Successfully</h4>
                <p className={styles.modalText}>
                    The product has been added to the system successfully.
                </p>
                <div className={styles.modalButtonContainer}>
                    <button
                        onClick={() => setSuccessModalOpen(false)}
                        className={styles.modalButton}
                    >
                        OK
                    </button>
                </div>
            </Modal>
        </div>
    );
};

export default ProductAdd;
